import { lazy, type ReactNode, Suspense, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, FolderOpen, History, Pencil } from 'lucide-react';
import { type DocumentDetail } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { humanSize } from '../../../lib/bytes';
import { useCurrentUserId } from '../../auth/auth-context';
import { projectFilesKeys } from '../../files/api';
import { FileTypeIcon } from '../../my-files/file-icon';
import { PreviewBody, type PreviewItem } from '../../my-files/preview/file-preview-dialog';
import { PreviewAction, PreviewFrame } from '../../my-files/preview/preview-frame';
import {
  CopyLinkAction,
  documentLinkHref,
  isLinkUnavailable,
} from '../../my-files/preview/preview-link';
import { PreviewSourceProvider } from '../../my-files/preview/preview-source';
import { documentApi, documentKeys } from '../api';
import { editableKindOf, editorNameOf } from '../editable-format';
import { PROJECT_FILES_SOURCE, projectDocumentStorage } from '../project-document-storage';
import { DeleteDocumentButton } from './delete-document-button';
import { DocumentDetailPanel, StatusBadge } from './document-detail-panel';
import { RenameDocumentButton } from './rename-document-button';
import {
  DocumentShareBadge,
  DocumentShareButton,
  useDocumentShare,
} from '../share/document-share-button';

const DocumentEditorDialog = lazy(() => import('../../my-files/editor/document-dialog'));
const SheetEditorDialog = lazy(() => import('../../my-files/sheet-editor/sheet-editor-dialog'));
const PptxEditorDialog = lazy(() => import('../../my-files/pptx-editor/pptx-editor-dialog'));
const MarkdownArticle = lazy(() =>
  import('../../my-files/preview/viewers/markdown-viewer').then((module) => ({
    default: module.MarkdownArticle,
  })),
);

/**
 * Xem trước một tài liệu dự án — APM-PDOC-d. Bấm một dòng ở khối Tài liệu (tab
 * Điều lệ) mở hộp này, **không** mở thẳng hộp soạn hay tải về.
 *
 * Thân: tệp của phiên bản mới nhất qua **đúng** bộ xem của trang Tệp
 * (`PreviewBody` — `.chilldoc`, `.md`, `.docx`, PDF, bảng tính, ảnh…), URL xin
 * từ tệp dự án (`PROJECT_FILES_SOURCE`). Định dạng không xem được thì bộ ấy tự
 * nói "chưa xem trước được" kèm nút tải về. Bản chỉ có chữ gõ tay hiện như
 * markdown.
 *
 * Khung và thanh nút là `PreviewFrame`, **chung** với trang Tệp — xem một tệp
 * ở dự án hay ở Tệp trông và bấm như nhau (người dùng xin 2026-10-02). Nút
 * chỉ-biểu-tượng, cùng thứ tự với trang Tệp: **Sửa** (định dạng hộp soạn hỗ trợ
 * — `editableKindOf`), **Đổi tên** (APM-DREN — người có quyền soạn), **Xem trong Tệp**, **Phiên bản & duyệt** (đổi thân sang
 * `DocumentDetailPanel`), **Chia sẻ** ra ngoài (APM-PDSH-b — mọi thành viên thấy
 * trạng thái, người có quyền duyệt đổi được), **Chép link** nội bộ (APM-PVLINK —
 * `documentLinkHref`, cho người đã có quyền đọc), **Tải về**, **Xoá** (PM), Đóng.
 * Đang chia sẻ thì dòng meta mang nhãn cấp chia sẻ.
 *
 * Mở theo link (`?doc=`) mà không đọc được — id lạ, dự án mình không ở, tài liệu
 * đã xoá — là **một** câu "không tìm thấy hoặc không có quyền" cho cả 404 lẫn 403.
 *
 * Mở hộp soạn thì hộp này **nhường chỗ** (gỡ ra, dựng lại khi đóng hộp soạn):
 * hai lớp phủ chồng nhau là hai chỗ nghe Esc, và mỗi lần Lưu đổi luôn tệp đang
 * xem — dựng lại là xem đúng bản vừa lưu.
 *
 * Toàn màn hình như trang Tệp. Nút "Mở rộng tối đa" (2026-09-30, khi đây còn
 * là hộp `lg`) đã bỏ: khung luôn lớn nhất rồi.
 *
 * Luật #3: nội dung đi thẳng S3 → trình duyệt bằng URL đã ký.
 *
 * ⚠️ Không nhận asOf: xem trạng thái **hiện tại** của một tài liệu, không vẽ
 * đại lượng nào theo thời gian — cùng lý do với khung xem trước của trang Tệp.
 */
export function DocumentPreviewDialog({
  documentId,
  projectId,
  canEdit,
  canDelete,
  onClose,
}: {
  readonly documentId: string;
  readonly projectId: string;
  readonly canEdit: boolean;
  readonly canDelete: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const me = useCurrentUserId();
  const [editing, setEditing] = useState(false);
  const [details, setDetails] = useState(false);
  // Ô trong header cho thanh công cụ của trình xem (zoom, trang, tìm).
  const [toolbarSlot, setToolbarSlot] = useState<HTMLElement | null>(null);

  const key = documentKeys.detail(documentId);
  const document = useQuery({ queryKey: key, queryFn: () => documentApi.detail(documentId) });
  const share = useDocumentShare(documentId);

  const storage = useMemo(
    () =>
      projectDocumentStorage(projectId, (saved) => {
        queryClient.setQueryData(documentKeys.detail(saved.id), saved);
        void queryClient.invalidateQueries({ queryKey: ['document'] });
        void queryClient.invalidateQueries({ queryKey: projectFilesKeys.list(projectId) });
      }),
    [projectId, queryClient],
  );

  const doc = document.data;
  const editable = doc ? editableKindOf(doc) : null;
  if (editing && doc && editable) {
    return (
      <Suspense fallback={null}>
        {editable.kind === 'sheet' ? (
          // Bảng tính (APM-XLE-d): hộp soạn Univer, cùng nơi lưu.
          <SheetEditorDialog
            item={{ id: doc.id, name: editable.filename }}
            storage={storage}
            onClose={() => setEditing(false)}
          />
        ) : editable.kind === 'pptx' ? (
          // PowerPoint (APM-PPX-i): hộp soạn pptx, cùng nơi lưu.
          <PptxEditorDialog
            item={{ id: doc.id, name: editable.filename }}
            storage={storage}
            onClose={() => setEditing(false)}
          />
        ) : (
          <DocumentEditorDialog
            target={{
              kind: 'edit',
              item: { id: doc.id, name: editable.filename ?? editorNameOf(doc, editable.format) },
            }}
            storage={storage}
            onClose={() => setEditing(false)}
          />
        )}
      </Suspense>
    );
  }

  const latest = doc?.versions[0];
  const file =
    latest?.fileId && latest.filename
      ? { id: latest.fileId, name: latest.filename, sizeBytes: latest.sizeBytes }
      : null;

  return (
    <PreviewFrame
      label={doc ? t('documents.preview.open', { title: doc.title }) : t('nav.documents')}
      // Bản chỉ có chữ gõ tay là markdown — cùng biểu tượng với `.md` ở trang Tệp.
      icon={<FileTypeIcon name={file?.name ?? 'content.md'} mimeType={null} />}
      title={doc?.title ?? t('nav.documents')}
      meta={
        doc ? (
          <>
            <span>{t(`documents.kind.${doc.kind}`)}</span>
            <StatusBadge status={doc.status} />
            <DocumentShareBadge share={share.data?.share} />
            <span className="truncate" title={file?.name}>
              {t('overview.documentVersion', { version: doc.latestVersionNo })}
              {file?.sizeBytes != null ? ` · ${humanSize(file.sizeBytes)}` : null}
            </span>
          </>
        ) : null
      }
      // Trang "Phiên bản & duyệt" không có trình xem: không chừa ô thanh công cụ cho nó.
      {...(details ? {} : { toolbarSlotRef: setToolbarSlot })}
      onClose={onClose}
      actions={
        doc ? (
          <PreviewActions
            doc={doc}
            file={file}
            me={me}
            canEdit={canEdit}
            canDelete={canDelete}
            editable={editable !== null}
            details={details}
            onEdit={() => setEditing(true)}
            onToggleDetails={() => setDetails((current) => !current)}
            onDeleted={onClose}
          />
        ) : null
      }
    >
      {document.isPending ? (
        <Skeleton className="m-4 h-64" />
      ) : document.isError ? (
        <div className="p-4">
          {isLinkUnavailable(document.error) ? (
            <ErrorState message={t('myFiles.preview.linkUnavailable')} />
          ) : (
            <ErrorState
              message={errorMessage(document.error, t)}
              onRetry={() => void document.refetch()}
            />
          )}
        </div>
      ) : details ? (
        <div className="mx-auto flex max-w-4xl flex-col gap-3 p-4">
          <Button
            size="sm"
            variant="ghost"
            className="self-start"
            onClick={() => setDetails(false)}
          >
            {t('documents.preview.back')}
          </Button>
          <DocumentDetailPanel
            documentId={documentId}
            canEdit={canEdit}
            canApprove={canDelete}
            onDeleted={onClose}
          />
        </div>
      ) : (
        <PreviewSourceProvider value={PROJECT_FILES_SOURCE}>
          <PreviewPane doc={document.data} toolbarSlot={toolbarSlot} />
        </PreviewSourceProvider>
      )}
    </PreviewFrame>
  );
}

/** Nút của khung — cùng kiểu, cùng thứ tự với `FilePreviewDialog`. */
function PreviewActions({
  doc,
  file,
  me,
  canEdit,
  canDelete,
  editable,
  details,
  onEdit,
  onToggleDetails,
  onDeleted,
}: {
  readonly doc: DocumentDetail;
  readonly file: { readonly id: string; readonly name: string } | null;
  readonly me: string | null;
  readonly canEdit: boolean;
  readonly canDelete: boolean;
  /** Có hộp soạn mở được bản mới nhất không. */
  readonly editable: boolean;
  readonly details: boolean;
  readonly onEdit: () => void;
  readonly onToggleDetails: () => void;
  readonly onDeleted: () => void;
}): ReactNode {
  const { t } = useTranslation();
  /*
    "Xem trong Tệp" mở trang Tệp **cá nhân**, thư mục "Tài liệu dự án", lọc theo
    tên tệp (người dùng chốt 2026-09-28). Trang ấy chỉ có tệp chính mình tải lên,
    nên với người khác nút khoá kèm lý do — một nút dẫn tới một danh sách trống
    là một nút nói dối.
  */
  const latest = doc.versions[0];
  const mine = latest !== undefined && me !== null && latest.createdBy === me;

  const download = async (fileId: string): Promise<void> => {
    try {
      const { url } = await PROJECT_FILES_SOURCE.downloadUrl(fileId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(errorMessage(error, t));
    }
  };

  return (
    <>
      {canEdit ? (
        <PreviewAction
          label={t('documents.preview.edit')}
          icon={<Pencil aria-hidden />}
          disabled={!editable}
          reason={t('documents.preview.editUnsupported')}
          onClick={onEdit}
        />
      ) : null}
      {canEdit ? <RenameDocumentButton document={doc} projectId={doc.projectId} inPreview /> : null}
      {file ? (
        <PreviewAction
          label={t('documents.preview.viewInFiles')}
          icon={<FolderOpen aria-hidden />}
          disabled={!mine}
          reason={t('documents.preview.viewInFilesOthers')}
          link={
            <Link to="/files" search={{ folder: 'document', q: file.name }}>
              <FolderOpen aria-hidden />
              <span className="sr-only">{t('documents.preview.viewInFiles')}</span>
            </Link>
          }
        />
      ) : null}
      <PreviewAction
        label={t('documents.preview.details')}
        icon={<History aria-hidden />}
        pressed={details}
        onClick={onToggleDetails}
      />
      <DocumentShareButton doc={doc} canWrite={canEdit} />
      <CopyLinkAction href={documentLinkHref(doc.projectId, doc.id)} />
      {file ? (
        <PreviewAction
          label={t('documents.preview.download')}
          icon={<Download aria-hidden />}
          onClick={() => void download(file.id)}
        />
      ) : null}
      {canDelete ? (
        <DeleteDocumentButton
          document={doc}
          projectId={doc.projectId}
          onDeleted={onDeleted}
          inPreview
        />
      ) : null}
    </>
  );
}

/** Thân xem trước: tệp của bản mới nhất, hoặc chữ gõ tay, hoặc "chưa có nội dung". */
function PreviewPane({
  doc,
  toolbarSlot,
}: {
  readonly doc: DocumentDetail;
  readonly toolbarSlot: HTMLElement | null;
}): ReactNode {
  const { t } = useTranslation();
  const latest = doc.versions[0];

  if (latest?.fileId && latest.filename) {
    const item: PreviewItem = {
      id: latest.fileId,
      name: latest.filename,
      // Không có MIME ở phiên bản: `viewerFor` chọn theo đuôi trước.
      mimeType: null,
      sizeBytes: latest.sizeBytes,
      kind: 'file',
    };
    return (
      <PreviewBody
        key={item.id}
        item={item}
        toolbarSlot={toolbarSlot}
        onDownload={() =>
          void PROJECT_FILES_SOURCE.downloadUrl(item.id).then(({ url }) =>
            window.open(url, '_blank', 'noopener,noreferrer'),
          )
        }
      />
    );
  }

  if (doc.latestContent?.trim()) {
    return (
      <Suspense fallback={<Skeleton className="m-4 h-40" />}>
        <MarkdownArticle markdown={doc.latestContent} />
      </Suspense>
    );
  }

  return <p className="p-6 text-sm text-muted-foreground">{t('documents.preview.empty')}</p>;
}
